import {Icon} from './Icon';
import { useId, useRef, useState, type FormEventHandler, type ReactNode } from 'react';

export type FilterBarProps = Readonly<{
  ariaLabel: string;
  search: ReactNode;
  primaryFilters?: ReactNode;
  secondaryFilters?: ReactNode;
  sort?: ReactNode;
  resultCount: ReactNode;
  activeFilterCount?: number;
  hasActiveCriteria?: boolean;
  onApply?: FormEventHandler<HTMLFormElement>;
  onInvalidCapture?: FormEventHandler<HTMLFormElement>;
  onClear?: () => void;
  applyLabel?: string;
  clearLabel?: string;
  filtersLabel?: string;
  busy?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}>;

export function FilterBar({
  ariaLabel,
  search,
  primaryFilters,
  secondaryFilters,
  sort,
  resultCount,
  activeFilterCount = 0,
  hasActiveCriteria = activeFilterCount > 0,
  onApply,
  onInvalidCapture,
  onClear,
  applyLabel = 'Apply filters',
  clearLabel = 'Clear filters',
  filtersLabel = 'Filters',
  busy = false,
  expanded,
  onExpandedChange,
}: FilterBarProps) {
  const filtersId = useId();
  const formRef = useRef<HTMLFormElement>(null);
  const [localExpanded, setLocalExpanded] = useState(false);
  const filtersExpanded = expanded ?? localExpanded;
  const hasDisclosureSlots = secondaryFilters !== undefined || sort !== undefined;
  const activeLabel = `${activeFilterCount} active filter${activeFilterCount === 1 ? '' : 's'}`;

  const submit: FormEventHandler<HTMLFormElement> = (event) => {
    if (onApply) onApply(event);
    else event.preventDefault();
  };

  function clear() {
    onClear?.();
    requestAnimationFrame(() => {
      formRef.current?.querySelector<HTMLElement>('input[type="search"], input[inputmode="search"]')?.focus();
    });
  }

  return <form
    ref={formRef}
    className="filter-bar"
    aria-label={ariaLabel}
    aria-busy={busy}
    onSubmit={submit}
    onInvalidCapture={onInvalidCapture}
  >
    <div className="filter-bar__layout">
      <div className="filter-bar__search">{search}</div>
      {primaryFilters !== undefined && <div className="filter-bar__primary">{primaryFilters}</div>}
      {hasDisclosureSlots && <button
        className="filter-bar__toggle"
        type="button"
        aria-controls={filtersId}
        aria-expanded={filtersExpanded}
        aria-label={`${filtersLabel}${activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}, ${activeLabel}`}
        onClick={() => { setLocalExpanded(!filtersExpanded); onExpandedChange?.(!filtersExpanded); }}
      >
        <Icon name="sliders-horizontal"/><span>{filtersLabel}{activeFilterCount > 0 && <>{' '}<span className="filter-bar__active-count" aria-hidden="true">({activeFilterCount})</span></>}</span>
      </button>}
      {hasDisclosureSlots && <div
        className={`filter-bar__filters${filtersExpanded ? ' filter-bar__filters--expanded' : ''}`}
        id={filtersId}
      >
        {secondaryFilters !== undefined && <div className="filter-bar__secondary">{secondaryFilters}</div>}
        {sort !== undefined && <div className="filter-bar__sort">{sort}</div>}
      </div>}
    </div>
    <div className="filter-bar__footer">
      <output className="filter-bar__result-count" role="status" aria-live="polite" aria-atomic="true">
        {resultCount}
      </output>
      <div className="filter-bar__actions">
        {onApply && <button type="submit" className="filter-bar__apply secondary">{applyLabel}</button>}
        {onClear && <button
          type="button"
          className="filter-bar__clear"
          disabled={!hasActiveCriteria}
          onClick={clear}
        >{clearLabel}</button>}
      </div>
    </div>
  </form>;
}
